import type { ReactNode } from 'react';
import { cx } from '../cx';
import type { ChartLayout } from './layout';

const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col items-start gap-3 @md:flex-row @md:items-center @md:gap-5',
  narrow: 'flex flex-col items-start gap-3',
  wide: 'flex flex-row items-center gap-5',
};
// In the narrow form the sentence leads and the grid follows it, wider.
const GRID: Record<ChartLayout, string> = {
  auto: 'order-last w-full max-w-56 @md:order-none @md:w-44',
  narrow: 'order-last w-full max-w-56',
  wide: 'w-44',
};

/** Of 100 comparable ads, how many pay less than `x` (rounded, 0 to 100). */
export function ofHundred(values: number[], x: number): number {
  if (!values.length) return 0;
  return Math.round((100 * values.filter((v) => v < x).length) / values.length);
}

export type ComparisonIconsProps = {
  /** How many of 100 comparable ads the sentence is about (from `ofHundred`). */
  count: number;
  /** The figure in words, large ("62 of 100"). */
  figure: ReactNode;
  /**
   * The rest of the sentence, naming the comparison group and its N: "comparable ads pay less than
   * your expected €4,300. Compared with 84 Helsinki software developer ads that state pay."
   */
  sentence: ReactNode;
  /** Chips to switch the comparison group (Helsinki, All Finland, Mid-level only). */
  groups?: ReactNode;
  /** `auto` (default) switches to the narrow form in a narrow container: the figure and sentence above a wider grid. */
  layout?: ChartLayout;
  className?: string;
};

/**
 * The reader's place as a count of 100 comparable ads in a 10 by 10 grid: natural frequencies read
 * better than percentiles. Ads compared with ads, never "you against the market", and never a
 * verdict. The reader's number stays on their device. The sentence is the accessible text; the
 * grid is decoration.
 */
export function ComparisonIcons({ count, figure, sentence, groups, layout = 'auto', className }: ComparisonIconsProps) {
  const n = Math.max(0, Math.min(100, Math.round(count)));
  return (
    <div className={cx('@container flex flex-col gap-4', className)}>
      <div className={ROW[layout]}>
        <div className={cx('grid shrink-0 grid-cols-10 gap-0.5', GRID[layout])} aria-hidden="true">
          {Array.from({ length: 100 }, (_, i) => (
            <span key={i} className={cx('aspect-square rounded-full', i < n ? 'bg-jc-ink' : 'bg-jc-chart-context')} />
          ))}
        </div>
        <p className="max-w-xs type-body text-jc-ink-secondary">
          <span className="block type-figure-lg text-jc-ink">{figure}</span>
          {sentence}
        </p>
      </div>
      {groups ? <div className="flex flex-wrap gap-2">{groups}</div> : null}
    </div>
  );
}
